import { DCC_SEND_STATUS } from 'Pods/ThreeDStudio/Store/slices/dccBridge'

import { BLENDER_PLUGIN_WS_URL } from './config'

// Implicit BE protocol contract for the Blender plugin (subject to change
// only via coordination with the plugin author):
//
//   Transport : WebSocket on `ws://localhost:49485`. Single-client server —
//               the plugin rejects a second concurrent connection with code
//               1008. Out-of-band liveness checks must use the HTTP /health
//               endpoint, not a WS handshake.
//   Request   : JSON `{ files, name, provider }` sent immediately after the
//               socket opens. `files` is `[{ url, filename }, ...]`; the
//               first entry is always the importable model file (e.g. .fbx)
//               and the remaining entries are companion assets (textures,
//               materials). `name` is the model's human-readable label
//               (run slug or session title). `provider` is one of
//               `meshy | tripo | tencent | cgt_converter` and lets the
//               plugin pick the right import adapter (axis conversion, etc.).
//   Statuses  : JSON `{ status, message }` where `status` is one of
//               `received | importing | sending | finished | success |
//                busy | error`. Anything else is treated as an error.
//
// If a future DCC plugin adopts a different transport, payload, or status
// vocabulary, give it its own sibling folder under Services/dccBridge/
// rather than retrofitting this file.

// Any local process can bind to the plugin port; cap status payloads so a hostile
// peer cannot force a multi-MB JSON.parse on the main thread.
const MAX_PAYLOAD_BYTES = 4096

const PLUGIN_TO_FE_STATUS = {
  connecting: DCC_SEND_STATUS.CONNECTING,
  received: DCC_SEND_STATUS.SENDING,
  importing: DCC_SEND_STATUS.SENDING,
  sending: DCC_SEND_STATUS.SENDING,
  finished: DCC_SEND_STATUS.SUCCESS,
  success: DCC_SEND_STATUS.SUCCESS,
  busy: DCC_SEND_STATUS.DCC_BUSY,
  error: DCC_SEND_STATUS.ERROR,
}

const mapStatus = (raw) => PLUGIN_TO_FE_STATUS[raw] || DCC_SEND_STATUS.ERROR

const isTerminal = (status) =>
  status === DCC_SEND_STATUS.SUCCESS ||
  status === DCC_SEND_STATUS.ERROR ||
  status === DCC_SEND_STATUS.DCC_BUSY

export const sendToBlender = ({ files, name, provider, onStatus }) => {
  let socket
  try {
    socket = new WebSocket(BLENDER_PLUGIN_WS_URL)
  } catch (e) {
    onStatus(DCC_SEND_STATUS.PLUGIN_NOT_RUNNING)
    return { close: () => {} }
  }

  let settled = false
  const settleWith = (feStatus) => {
    if (settled) return
    settled = true
    onStatus(feStatus)
    try { socket.close() } catch { /* noop */ }
  }

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({ files, name, provider }))
  })

  socket.addEventListener('message', (event) => {
    if (typeof event.data !== 'string' || event.data.length > MAX_PAYLOAD_BYTES) {
      settleWith(DCC_SEND_STATUS.ERROR)
      return
    }

    let payload
    try {
      payload = JSON.parse(event.data)
    } catch {
      settleWith(DCC_SEND_STATUS.ERROR)
      return
    }

    const feStatus = mapStatus(payload?.status)
    if (isTerminal(feStatus)) {
      settleWith(feStatus)
    } else {
      onStatus(feStatus)
    }
  })

  socket.addEventListener('error', () => {
    settleWith(DCC_SEND_STATUS.PLUGIN_NOT_RUNNING)
  })

  socket.addEventListener('close', () => {
    if (!settled) settleWith(DCC_SEND_STATUS.PLUGIN_NOT_RUNNING)
  })

  return { close: () => { try { socket.close() } catch { /* noop */ } } }
}
